import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { take } from 'rxjs';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import * as fromLayout from '../layout';
import { LayoutActions } from '../layout/store/layout.actions';
import { ConnectorListRowComponent } from './connector-list-row.component';
import { ConnectorsActions } from './connectors.actions';
import { ConnectorProvider, ConnectorView } from './connectors.models';
import { connectorsFeature, ConnectorsState } from './connectors.reducer';

/** Lower-case and strip accents, so a typed "e" also matches "é" and "É". */
const foldForSearch = (text: string): string =>
  text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

/** The filter the tab strip applies to the list. */
type ConnectorsTab = 'all' | 'connected' | 'notConnected';

/**
 * The Connectors section of the settings modal (Figma: section 13889-1984, mobile 13025-34029,
 * header 13029-34964, empty search 13029-36502) — every connector this deployment offers, each with
 * its own connect, re-authenticate and disconnect, filtered by a tab strip and a search field. Its
 * header stays put and only the list below it scrolls.
 *
 * Filtering is local: the list arrives whole, so a tab and a query are two signals over it rather
 * than a round trip. The settings modal creates this on every open, so each open starts on "All"
 * with an empty search.
 */
@Component({
  selector: 'squadbox-connectors-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col flex-1 min-h-0' },
  imports: [
    TranslateModule,
    ConnectorListRowComponent,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.InputComponent,
    fromSeriousSystem.SpinnerDirective,
    fromSeriousSystem.TabsComponent,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  template: `
    <div class="relative flex flex-col flex-1 min-h-0 text-neutral-900">
      <div
        class="shrink-0 flex flex-col gap-2 px-4 pt-4 border-b border-neutral-200"
      >
        <div class="relative h-8">
          <!-- A phone shows the panes as a list first; this goes back to it (SB-6520 KD-9). -->
          @if (showBack()) {
            <button
              sdIconButton
              variant="icon"
              color="neutral"
              size="xs"
              class="absolute top-0 left-0"
              [attr.aria-label]="'SETTINGS.BACK' | translate"
              (click)="back.emit()"
            >
              <svg sdUseArrowIcon="arrow-left"></svg>
            </button>
          }
          <button
            sdIconButton
            variant="icon"
            color="neutral"
            size="xs"
            class="absolute top-0 right-0"
            [attr.aria-label]="'SETTINGS.CLOSE' | translate"
            (click)="handleClose()"
          >
            <svg sdUseUIBasicIcon="close"></svg>
          </button>
        </div>

        <div class="flex items-center px-4">
          <h2 class="flex-1 min-w-0 typo-h6 leading-8 font-semibold truncate">
            {{ 'CONNECTORS.TITLE' | translate }}
          </h2>
        </div>

        <!--
            One 40px row on desktop, tabs left and search right. Below
            tablet-landscape the two stack with the search on top, which keeps
            the tab strip against the header's bottom border where its selected
            underline belongs.
          -->
        <div
          class="flex flex-col-reverse gap-4 px-4 tablet-landscape:flex-row tablet-landscape:items-start tablet-landscape:gap-4"
        >
          <!-- leading-5 because sd-modal's body sets leading-6 and the tab
                 labels would otherwise inherit a 24px line box for P3 14/20. -->
          <sd-tabs
            class="flex-1 min-w-0 leading-5"
            [tabOptions]="tabOptions()"
            [selectedTabKey]="activeTab()"
            [isMobile]="isMobile()"
            [compact]="true"
            [showBorderBottomNeutral]="false"
            (tabClicked)="activeTab.set($event.key)"
          />

          <!-- leading-5 for the same reason as the tab strip above: the field
                 inherits sd-modal's leading-6 and P3 is 14/20. -->
          <div class="shrink-0 w-full leading-5 tablet-landscape:w-60">
            <sd-input
              [label]="''"
              [leadingIcon]="'search'"
              [searchVariant]="true"
              [clearable]="true"
              [clearAriaLabel]="
                translateService.instant('CONNECTORS.ACTIONS.CLEAR_SEARCH')
              "
              [placeholder]="'CONNECTORS.SEARCH_PLACEHOLDER' | translate"
              (valueChanged)="searchTerm.set($event)"
            />
          </div>
        </div>
      </div>

      <div
        #scroller
        class="flex flex-col flex-1 min-h-0 overflow-y-auto px-5 pb-4 tablet-landscape:px-8 tablet-landscape:pt-4"
      >
        <!-- Always present, so the fade's observer sees every content change, whichever state shows. -->
        <div class="flex flex-col flex-1">
          @if (isLoading() && !connectors().length) {
            <div class="flex flex-1 items-center justify-center py-10">
              <div sdSpinner></div>
            </div>
          } @else if (!connectors().length) {
            <p [class]="emptyStateClass">
              {{ 'CONNECTORS.EMPTY' | translate }}
            </p>
          } @else if (!tabConnectors().length) {
            <!-- Before the search message: an empty tab stays empty whatever is typed. -->
            <p [class]="emptyStateClass">
              {{ tabEmptyKey() | translate }}
            </p>
          } @else if (!visibleConnectors().length) {
            <p [class]="emptyStateClass">
              {{ 'CONNECTORS.SEARCH_EMPTY' | translate }}
            </p>
          } @else {
            <div>
              @for (
                connector of visibleConnectors();
                track connector.provider
              ) {
                <squadbox-connector-list-row
                  [connector]="connector"
                  [isMobile]="isMobile()"
                  [isBusy]="disconnecting() === connector.provider"
                  (connect)="handleConnect(connector.provider)"
                  (reauthenticate)="handleConnect(connector.provider)"
                  (disconnect)="handleDisconnect(connector)"
                />
              }
            </div>
          }
        </div>
      </div>

      <!-- Spec A2 / Figma "Table Fade" (13029-36743): 48px, while rows remain below. A sibling of
           the scroller, not a child, so it stays on the bottom edge instead of scrolling away. It
           stops at the measured scrollbar gutter: 0 with overlay scrollbars, ~15px with classic
           ones, which it would otherwise cover. -->
      @if (hasMoreBelow()) {
        <div
          aria-hidden="true"
          class="pointer-events-none absolute left-0 bottom-0 h-12 bg-gradient-to-t from-shades-white to-transparent"
          [style.right.px]="scrollbarGutter()"
        ></div>
      }
    </div>
  `,
})
export class ConnectorsPanelComponent {
  /** Whether the header offers the way back to the settings pane list (phones). */
  public readonly showBack = input(false);
  public readonly back = output();
  private readonly store = inject(Store<ConnectorsState>);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  public readonly translateService = inject(TranslateService);

  protected readonly connectors = this.store.selectSignal(
    connectorsFeature.selectConnectors,
  );
  protected readonly isLoading = this.store.selectSignal(
    connectorsFeature.selectIsLoading,
  );
  protected readonly disconnecting = this.store.selectSignal(
    connectorsFeature.selectDisconnecting,
  );

  /**
   * The same 904px threshold the `tablet-landscape:` classes above switch on,
   * for the two places a class can't reach: `sd-tabs`, which takes it as an
   * input, and the row, which drops its description line and shrinks its
   * connected badge to a disc.
   */
  protected readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );

  /**
   * An empty list is centred in the content area, which the settings modal's
   * fixed height keeps the same whichever tab is selected.
   */
  protected readonly emptyStateClass =
    'flex flex-1 items-center justify-center typo-p3 leading-5 text-center text-neutral-400';

  protected readonly activeTab = signal<ConnectorsTab>('all');
  protected readonly searchTerm = signal('');
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  /** Rows remain below the visible part of the list, so the bottom fade shows. */
  protected readonly hasMoreBelow = signal(false);

  /** The scroller's scrollbar width (it has no border, so `offsetWidth − clientWidth` is all scrollbar). */
  protected readonly scrollbarGutter = signal(0);

  constructor() {
    /**
     * Same approach as `sd-response-table`'s fades: re-measure on scroll, and on resize of the
     * scroller (viewport changes, the pane opening) and of its content (a tab or search changing
     * the rows). Setting an unchanged boolean is a no-op, so scrolling costs no change detection.
     */
    effect((onCleanup) => {
      const scroller = this.scroller()?.nativeElement;
      if (!scroller) {
        return;
      }
      const measure = (): void => {
        this.hasMoreBelow.set(
          scroller.scrollTop + scroller.clientHeight <
            scroller.scrollHeight - 1,
        );
        this.scrollbarGutter.set(scroller.offsetWidth - scroller.clientWidth);
      };

      scroller.addEventListener('scroll', measure, { passive: true });
      const observer = new ResizeObserver(measure);
      observer.observe(scroller);
      if (scroller.firstElementChild) {
        observer.observe(scroller.firstElementChild);
      }

      onCleanup(() => {
        scroller.removeEventListener('scroll', measure);
        observer.disconnect();
      });
    });
  }

  protected readonly tabOptions = computed<
    fromSeriousSystem.TabOption<ConnectorsTab>[]
  >(() => [
    {
      key: 'all',
      label: this.translateService.instant('CONNECTORS.TABS.ALL') as string,
    },
    {
      key: 'connected',
      label: this.translateService.instant(
        'CONNECTORS.TABS.CONNECTED',
      ) as string,
    },
    {
      key: 'notConnected',
      label: this.translateService.instant(
        'CONNECTORS.TABS.NOT_CONNECTED',
      ) as string,
    },
  ]);

  /**
   * The tab narrows by link state over the loaded list. A connector the
   * provider no longer honours (`needs_reconnect`) counts as connected: it is
   * linked, just stale, and its fix — "Re-authenticate" — lives in the row's
   * menu, which only a connected row draws.
   */
  protected readonly tabConnectors = computed(() => {
    const tab = this.activeTab();

    return this.connectors().filter((connector) => {
      const isLinked = connector.status !== null;
      return tab === 'all' || (tab === 'connected' ? isLinked : !isLinked);
    });
  });

  /** The query narrows the tab's rows by name only, ignoring case and accents (spec A4). */
  protected readonly visibleConnectors = computed(() => {
    const query = foldForSearch(this.searchTerm().trim());
    if (!query) {
      return this.tabConnectors();
    }

    return this.tabConnectors().filter((connector) =>
      foldForSearch(connector.displayName).includes(query),
    );
  });

  /**
   * Only "Connected" and "Not connected" can run empty while connectors are
   * offered: "All" empty means nothing is offered, which `CONNECTORS.EMPTY`
   * covers first.
   */
  protected readonly tabEmptyKey = computed(() =>
    this.activeTab() === 'connected'
      ? 'CONNECTORS.TAB_EMPTY.CONNECTED'
      : 'CONNECTORS.TAB_EMPTY.NOT_CONNECTED',
  );

  /** Re-authenticating is the same consent round trip as connecting; the provider replaces the grant. */
  protected handleConnect(provider: ConnectorProvider): void {
    this.store.dispatch(ConnectorsActions.connectProvider({ provider }));
  }

  protected handleClose(): void {
    this.store.dispatch(LayoutActions.closeSettings());
  }

  protected handleDisconnect(connector: ConnectorView): void {
    // Disconnecting revokes a grant at the provider and deletes the stored credentials, so it is
    // confirmed rather than immediate — the same treatment every other destructive action gets.
    this.confirmDialogService
      .confirm({
        title: this.translateService.instant(
          'CONNECTORS.DISCONNECT_CONFIRM.TITLE',
        ) as string,
        message: this.translateService.instant(
          'CONNECTORS.DISCONNECT_CONFIRM.MESSAGE',
          { provider: connector.displayName },
        ) as string,
        confirmLabel: this.translateService.instant(
          'CONNECTORS.ACTIONS.DISCONNECT',
        ) as string,
        cancelLabel: this.translateService.instant(
          'CONNECTORS.DISCONNECT_CONFIRM.CANCEL',
        ) as string,
        confirmType: 'delete',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            ConnectorsActions.disconnectProvider({
              provider: connector.provider,
            }),
          );
        }
      });
  }
}
